import {
	CallType,
	ContactRoleType,
	DocumentEntity,
	DocumentsOverviewQueryQuery,
	DocumentStatus,
	SalesCharter,
	ProcessDocumentAction,
} from "__generated__/graphql";
import { Button, FormGroup, Input, showSnackbar, Textarea } from "@cofano/cck";
import CallTypeSelect from "components/selects/CallTypeSelect";
import { hasPluginPermission } from "components/utils/Permission";
import { useStoreState } from "ezStore";
import Documents, { DocumentOptionalColumns, Entity } from "components/documents/Documents";
import { useEffect, useRef, useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { cargoActionTypeToDocumentType } from "components/documents/DocumentsUtils";
import { CustomDialog } from "components/dialogs/Dialogs";
import { Tooltip } from "@mui/material";
import {
	mergeDocuments_mutation,
	processDocuments_mutation,
	publishDocuments_mutation,
	extractDocument_mutation,
	parseExtractPageNumbers,
} from "view/documents/utils/DocumentUtils";
import Grid from "@mui/material/Grid2";
import { Relation } from "view/relations/types/relation";
import TimesheetGenerationDialog from "view/documents/cargos/components/dialogs/TimesheetGenerationDialog";
import CargoDocumentCargoSelect from "view/documents/cargos/components/selects/CargoDocumentCargoSelect";

type Cargo = DocumentsOverviewQueryQuery["actionsPerCharterPerShip"]["content"][number]["charterWithCargoActions"][number]["charter"]["cargos"][number];

type Document = {
	id: number;
	status: DocumentStatus;
	documentEntity: DocumentEntity;
	type: string;
	cargoActionId?: number | null;
};

type DocumentProcessingDialogProps = {
	open: boolean;
	onClose: () => void;
	refetch: () => void;
	charter: Pick<SalesCharter, "id" | "charterNumber" | "contacts"> & { debtor?: Pick<Relation, "name"> | null, broker?: Pick<Relation, "name"> | null };
	cargo: Cargo;
	allCargos: Cargo[];
};

type ExtractPagesButtonProps = {
	document: Document;
	callId?: number;
	cargoId: number;
	refetch: () => void;
	refetchTable: () => void;
	onClose: () => void;
}

const DocumentProcessingDialog = (
	{
		open,
		onClose,
		refetch: refetchTable,
		charter,
		cargo,
		allCargos,
	}: DocumentProcessingDialogProps) => {

	const session = useStoreState(store => store.session);
	const additionalActionTypes = hasPluginPermission(session, "cargoActionUsePurgingAndCleaningTypes") ? [ CallType.Cleaning, CallType.NitrogenPurging ] : [];

	const [ mergeDocuments ] = useMutation(mergeDocuments_mutation);

	const callId = cargo.actions[0].call?.id;

	const [ entities, setEntities ] = useState<Entity[]>([]);
	const [ documents, setDocuments ] = useState<Document[]>([]);
	const [ selectedCargos, setSelectedCargos ] = useState<Cargo[]>(allCargos);
	const [ cargoActionTypes, setCargoActionTypes ] = useState<CallType[]>([ ...new Set(allCargos.flatMap(x => x.actions).map(x => x.type)) ]);
	const [ timesheetDialogOpen, setTimesheetDialogOpen ] = useState(false);

	// Captured from the inner Documents component so the timesheet dialog can refresh its list after generating.
	const documentsRefetchRef = useRef<(() => void) | undefined>(undefined);

	const handleMerge = (selection: Document[], refetch: () => void) => {
		if (callId == null) {
			return;
		}

		mergeDocuments({
			variables: {
				input: {
					action: ProcessDocumentAction.Merge,
					documentEntity: selection[0].documentEntity,
					callId: callId,
					cargoId: cargo.id,
					cargoActionId: selection[0].cargoActionId,
					documentIds: selection.map(s => s.id),
				},
			},
		}).then(() => {
			refetch();
			refetchTable();
		});
	};

	const searchToEntities = (cargos: Cargo[], cargoActionTypes: CallType[]): Entity[] => {
		if (cargos.length == 0) {
			cargos = allCargos;
		}

		if (cargoActionTypes.length == 0) {
			cargoActionTypes = [ CallType.Loading, CallType.Discharging, CallType.Cleaning, CallType.NitrogenPurging ];
		}

		const entities: Entity[] = [];

		for (const cargo of cargos) {
			for (const action of cargo.actions ?? []) {
				if (action.type == null || !cargoActionTypes.includes(action.type)) {
					continue;
				}
				entities.push({
					id: cargo.id,
					cargoActionId: action.id,
					type: cargoActionTypeToDocumentType(action.type),
					name: cargo.cargoNumber || cargo.containerNumber || cargo.product?.name || cargo.product?.description || "unknown",
				});
			}
		}

		return entities;
	};

	// Initial load
	useEffect(() => {
		setEntities(searchToEntities(selectedCargos, cargoActionTypes));
	}, []);

	return <>
		<CustomDialog
			maxWidth="lg"
			open={open && !timesheetDialogOpen}
			onClose={onClose}
			title={`${intl.formatMessage({ id: "CARGO_PROCESSING_DIALOG_TITLE" })} - ${charter.debtor?.name} ${charter.broker ? `- ${charter.broker.name}` : ""} - ${charter.charterNumber}`}
		>
			<Grid container spacing={2}>
				<Grid size={3}>
					<FormGroup label={intl.formatMessage({ id: "CARGO" })}>
						<CargoDocumentCargoSelect
							cargos={allCargos}
							value={selectedCargos}
							onChange={cargos => {
								setSelectedCargos(allCargos.filter(c => cargos?.some(selected => selected.id === c.id)));
								setEntities(searchToEntities(allCargos.filter(c => cargos?.some(selected => selected.id === c.id)), cargoActionTypes));
							}}
						/>
					</FormGroup>
				</Grid>
				<Grid size={9}>
					<FormGroup label={intl.formatMessage({ id: "CARGO_ACTION_TYPE" })}>
						<CallTypeSelect
							multiple
							value={cargoActionTypes}
							clearable
							onChange={callTypes => {
								setCargoActionTypes(callTypes ?? []);
								setEntities(searchToEntities(selectedCargos, callTypes ?? []));
							}}
							additionalOptions={additionalActionTypes} />
					</FormGroup>
				</Grid>
			</Grid>

			<Documents
				entities={[ { id: 0, type: DocumentEntity.Cargo, name: "" }, ...entities ]}
				canCreate={false}
				showOptionalColumns={[ DocumentOptionalColumns.Status, DocumentOptionalColumns.LastStatusUpdateOn, DocumentOptionalColumns.Entity ]}
				onDocumentsLoaded={setDocuments}
				additionalDrawerActions={(refetch, closeDrawer) => [
					{
						render: (document) => <ExtractPagesButton
							document={document}
							callId={callId}
							cargoId={cargo.id}
							refetch={refetch}
							refetchTable={refetchTable}
							onClose={closeDrawer}
						/>,
					},
				]}
				additionalActions={(refetch) => [
					{
						render: () =>
							<Button
								size="small"
								variant="outlined"
								onClick={() => {
									documentsRefetchRef.current = refetch;
									setTimesheetDialogOpen(true);
								}}>
								{intl.formatMessage({ id: "GENERATE_TIMESHEET" })}
							</Button>,
					},
					{
						render: (selection) =>
							<ProcessButton
								charter={charter}
								documents={documents}
								selection={selection ?? []}
								refetch={refetch}
								refetchTable={refetchTable} />,

					},
					{
						render: (selection) =>
							<PublishButton
								charter={charter}
								documents={documents}
								selection={selection ?? []}
								refetch={refetch}
								refetchTable={refetchTable} />,
					},
					{
						title: intl.formatMessage({ id: "MERGE" }),
						disabled: (selection: Document[]) => selection.length < 2 || selection.some(s => s.type !== "pdf"),
						onClick: (selection) => handleMerge(selection ?? [], refetch),
					},
				]}
			/>
		</CustomDialog>

		<TimesheetGenerationDialog
			open={timesheetDialogOpen}
			onClose={() => setTimesheetDialogOpen(false)}
			onRefetch={() => {
				documentsRefetchRef.current?.();
				refetchTable();
			}}
			cargos={selectedCargos}
		/>
	</>;
};

type ActionButtonProps = {
	charter: Pick<SalesCharter, "id" | "contacts">;
	documents: Document[];
	selection: Document[];
	refetch: () => void;
	refetchTable: () => void;
};
const ProcessButton = ({ documents, selection, refetch, refetchTable }: ActionButtonProps) => {
	const [ processDocumentsMutation, { loading } ] = useMutation(processDocuments_mutation);

	const processDocuments = async () => {
		const documentIds =
			(selection.length === 0 ? documents : selection)
				.filter(doc => doc.status === DocumentStatus.Created)
				.map(doc => doc.id);

		await processDocumentsMutation({ variables: { ids: documentIds } });

		refetch();
		refetchTable();
	};

	return <Button
		size="small"
		variant="outlined"
		disabled={loading || selection.length == 0 || selection.some(d => d.status !== DocumentStatus.Created)}
		loading={loading}
		onClick={processDocuments}>
		{intl.formatMessage({ id: "PROCESS" })}
	</Button>;

};

const PublishButton = ({ charter, documents, selection, refetch, refetchTable }: ActionButtonProps) => {
	const [ publishDocumentsMutation, { loading } ] = useMutation(publishDocuments_mutation);
	const [ dialogOpen, setDialogOpen ] = useState(false);
	const [ notes, setNotes ] = useState("");

	const contacts = charter.contacts?.filter(c => c.contactRoleType == ContactRoleType.CargoDocuments) ?? [];
	const hasContacts = contacts.length > 0;

	const publishDocuments = async (sendEmail: boolean) => {
		await publishDocumentsMutation({
			variables: {
				charterId: charter.id,
				publishIds: selection.map(s => s.id),
				sendEmailIds: sendEmail ? selection.map(s => s.id) : [],
				sendEmail: sendEmail,
				processIds: documents
					.filter(doc => !selection.some(s => s.id === doc.id))
					.filter(doc => doc.status === DocumentStatus.Created)
					.map(s => s.id),
				notes: notes || undefined,
			},
		});

		showSnackbar(intl.formatMessage({ id: "CARGO_DOCUMENTS_PUBLISH_EMAIL_SENT" }), { variant: "success" });

		setDialogOpen(false);
		refetch();
		refetchTable();
	};

	return (
		<>
			<Button
				size="small"
				variant="outlined"
				loading={loading}
				disabled={loading || selection.length == 0}
				onClick={() => setDialogOpen(true)}>
				{intl.formatMessage({ id: "DOCUMENTS_PUBLISH" })}
			</Button>

			<CustomDialog
				open={dialogOpen}
				onClose={() => {
					setDialogOpen(false);
					setNotes("");
				}}
				maxWidth="sm"
				title={intl.formatMessage({ id: "CARGO_DOCUMENTS_PUBLISH_DIALOG_TITLE" })}
				actions={<>
					<Button
						onClick={() => publishDocuments(false)}
						disabled={loading}
						loading={loading}
						size="small"
						variant="outlined">
						{intl.formatMessage({ id: "PUBLISH" })}
					</Button>
					<Tooltip title={!hasContacts ? intl.formatMessage({ id: "CARGO_DOCUMENTS_NO_CONTACTS" }) : ""} placement="top">
						<span>
							<Button
								onClick={() => publishDocuments(true)}
								disabled={loading || !hasContacts}
								loading={loading}
								size="small">
								{intl.formatMessage({ id: "PUBLISH_AND_SEND" })}
							</Button>
						</span>
					</Tooltip>
				</>}>
				<div style={{ display: "flex", flexDirection: "column" }}>
					<span style={{ paddingBottom: 5 }}>
						{intl.formatMessage({ id: "CARGO_DOCUMENTS_PUBLISH_DIALOG_CONTENT" })}
					</span>
					{contacts
						.map(crt => crt.contact)
						.map((co, index) => (
							<li key={index} style={{ marginLeft: 16 }}>
								{`${co.name}${co.email ? " (" + co.email + ")" : ""}`}
							</li>
						))}
				</div>
				<div style={{ marginTop: 16 }}>
					<FormGroup label={intl.formatMessage({ id: "NOTE" })}>
						<Textarea value={notes} onChange={e => setNotes(e.target.value)} />
					</FormGroup>
				</div>
			</CustomDialog>
		</>
	);
};

const ExtractPagesButton = ({ document, callId, cargoId, refetch, refetchTable, onClose }: ExtractPagesButtonProps) => {
	const [ extractDocument, { loading } ] = useMutation(extractDocument_mutation);
	const [ pageNumbers, setPageNumbers ] = useState<string | undefined>(undefined);

	const handleExtract = () => {
		if (callId == null) {
			return;
		}

		const pages = parseExtractPageNumbers(pageNumbers);

		extractDocument({
			variables: {
				input: {
					action: ProcessDocumentAction.Extract,
					callId: callId,
					cargoId: cargoId,
					cargoActionId: document.cargoActionId,
					documentEntity: document.documentEntity,
					documentIds: [ document.id ],
					pageNumbers: pages,
				},
			},
		}).then(() => {
			refetch();
			refetchTable();
			onClose();
		});
	};


	return (
		<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
			<FormGroup label={intl.formatMessage({ id: "PAGE_NUMBERS" })} labelPlacement="left-center">
				<Input
					value={pageNumbers}
					onChange={(e) => setPageNumbers(e.target.value)}
					placeholder={`${intl.formatMessage({ id: "EXAMPLE" })}: 1, 2-4, 6`} />
			</FormGroup>
			<Button
				loading={loading}
				size="small"
				variant="outlined"
				onClick={() => handleExtract()}>{intl.formatMessage({ id: "EXTRACT" })}</Button>
		</div>
	);
};

export default DocumentProcessingDialog;
